import {
  cloneElement,
  isValidElement,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import styled, { css } from "styled-components";

import { AnchoredPopup, useAnchoredPopupAnchor } from "../../AnchoredPopup";
import { FieldContextProvider, useFieldControl } from "../../Field/FieldContext";
import { IconButton } from "../../IconButton";
import { Text, type TextVariantDefinition } from "../../Text";
import { CheckIcon as CheckGlyph, ChevronIcon as ChevronGlyph } from "../../../icons/glyphs";
import {
  controlRadius,
  controlTransition,
  inlineElementHeight,
  inlineElementIndicatorSize,
} from "../../foundations/controlStyles";
import type { DropdownItem } from "../../foundations/option";
import { type FloatingPopoverPlacement } from "../../foundations/popover";
import { yScrollbarStyles } from "../../foundations/scrollbar";
import { DropdownSearchField } from "../DropdownSearchField";
import { useDropdownOpenChange, type DropdownOpenChangeHandler } from "../dropdownOpenChange";
import { defaultDropdownFilterOption } from "../dropdownSearch";
import {
  dropdownTriggerGap,
  dropdownTriggerPadding,
  dropdownTriggerTextVariant,
  resolveDropdownMenuWidth,
  type DropdownMenuWidth,
  type DropdownMode,
} from "../common";

export type SingleSelectDropdownOption = DropdownItem & {
  count?: number;
};

export type SingleSelectDropdownMenuAlign = "left" | "right";
export type SingleSelectDropdownMenuWidth = DropdownMenuWidth;

/** Trigger density. `md` (default) is the standard control height; `sm` is a compact trigger
 * (shorter + tighter vertical padding + smaller label; side padding stays on the shared
 * controlPadX scale) for dense filter bars. Only the trigger changes —
 * the menu/options keep the same size. */
export type SingleSelectDropdownSize = "sm" | "md";
/**
 * Semantic display mode of the control.
 *
 * - `field` (default): shows only the selected value while retaining `label: value` as the
 *   accessible name, and keeps the closed trigger neutral.
 * - `facet`: shows `label: value` and emphasizes an active selection — for filter facets.
 */
export type SingleSelectDropdownMode = DropdownMode;
export type SingleSelectDropdownPlacement = Extract<
  FloatingPopoverPlacement,
  "top-start" | "top-end" | "bottom-start" | "bottom-end"
>;

export type SingleSelectDropdownOptionRenderContext = {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
};

export type SingleSelectDropdownFilterOption = (
  option: SingleSelectDropdownOption,
  query: string,
) => boolean;

/** 선택된 trigger의 의미 구간. facet은 prefix/value, field는 value만 렌더한다. */
export type SingleSelectDropdownTriggerSegment = "prefix" | "value";

/** trigger 구간(`prefix`/`value`)별 스타일 override 맵. 지정한 구간만 기본값을 대체. */
export type SingleSelectDropdownLabelStyle = Partial<
  Record<SingleSelectDropdownTriggerSegment, TextVariantDefinition>
>;

export type SingleSelectDropdownProps = {
  /** Trigger id. Inside Field, the Field id is the single source of truth. */
  id?: string;
  /** Standalone trigger fallback and accessible/popup label. Compose with Field for a visible title. */
  label: string;
  options: readonly SingleSelectDropdownOption[];
  value: string | null;
  onValueChange: (value: string) => void;
  /** 실제 popup open 상태가 바뀐 뒤 호출한다. 최초 mount와 callback identity 변경만으로는 호출하지 않는다. */
  onOpenChange?: DropdownOpenChangeHandler;
  placeholder?: string;
  /**
   * facet trigger를 강조(accent)하지 않을 "중립/기본" 값. `value`가 이 값과 같으면 메뉴엔 그
   * 옵션이 정상 체크되지만 트리거는 강조하지 않는다("없음/전체" 같은 default 옵션용).
   * 기본(미지정)에선 임의의 string 값(`""` 포함)을 정상 선택으로 강조한다.
   * field trigger는 이 prop과 무관하게 닫힌 상태가 항상 neutral이다.
   */
  neutralValue?: string;
  /** 의미적 표시 모드. `field`(기본)는 일반 필드 표현, `facet`은 filter facet 요약을 사용한다. */
  mode?: SingleSelectDropdownMode;
  emptyMessage?: string;
  /** When set, an emphasized `(*)` marker is shown in the placeholder until a value is chosen. */
  required?: boolean;
  disabled?: boolean;
  /** trigger가 컨테이너 폭을 채운다(폼 필드 정렬용). 기본은 content-width. `BaseInput.fullWidth` 관례와 동일. */
  fullWidth?: boolean;
  /** trigger density. `md`(기본) 표준 높이, `sm` 컴팩트(dense filter bar용). 메뉴/옵션은 동일 크기. */
  size?: SingleSelectDropdownSize;
  className?: string;
  style?: CSSProperties;
  menuAlign?: SingleSelectDropdownMenuAlign;
  /**
   * 메뉴 폭. CSS 길이 또는 `"matchTrigger"`를 받는다. 메뉴는 portal의 fixed overlay라 `%`는
   * trigger가 아닌 viewport 기준이므로 trigger와 맞추려면 `"matchTrigger"`를 사용한다.
   */
  menuWidth?: SingleSelectDropdownMenuWidth;
  maxMenuHeight?: string;
  placement?: SingleSelectDropdownPlacement;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Accessible name for the search-query clear button. */
  clearSearchLabel?: string;
  searchAction?: ReactNode;
  noResultsMessage?: string;
  /**
   * 선택된 trigger 구간별 스타일 override. `prefix`(= facet의 "label: "),
   * `value`(= 선택된 라벨) 키를 받는다. facet 기본은 prefix를 muted/regular로 덜 강조하고
   * value는 trigger 색을 상속한다. field는 value만 렌더한다. 지정한 키만 기본값을 대체한다.
   */
  labelStyle?: SingleSelectDropdownLabelStyle;
  filterOption?: SingleSelectDropdownFilterOption;
  renderOption?: (
    option: SingleSelectDropdownOption,
    context: SingleSelectDropdownOptionRenderContext,
  ) => ReactElement;
};

const defaultEmptyMessage = "No options.";
const defaultNoResultsMessage = "No results.";
const defaultSearchPlaceholder = "Search...";
const defaultClearSearchLabel = "Clear search";
const defaultMenuWidth = "min(22rem, calc(100vw - 2rem))";
const defaultMaxMenuHeight = "20rem";

const boldWeight = "var(--c6-typography-weight-bold)";
const regularWeight = "var(--c6-typography-weight-regular)";
const mutedColor = "var(--c6-color-text-muted)";

// 선택된 facet trigger에서 "label:" 프리픽스를 accent value보다 덜 강조하는 기본 스타일.
// value 구간은 기본 스타일이 없어 facet trigger의 accent 색을 그대로 상속한다.
const defaultTriggerLabelStyle: SingleSelectDropdownLabelStyle = {
  prefix: {
    as: "span",
    style: { color: mutedColor, fontWeight: regularWeight },
  },
};
const disabledTriggerLabelStyle: SingleSelectDropdownLabelStyle = {
  prefix: {
    as: "span",
    style: { color: "inherit", fontWeight: regularWeight },
  },
};

const emptyTextVariant = {
  as: "span",
  style: {
    color: "var(--c6-color-text-muted)",
    fontSize: "var(--c6-typography-size-body)",
    lineHeight: "var(--c6-typography-line-height-normal)",
  },
} satisfies TextVariantDefinition;

const optionLabelTextVariant = {
  as: "span",
  style: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    color: "inherit",
    fontSize: "var(--c6-typography-size-body)",
    fontWeight: regularWeight,
    lineHeight: "var(--c6-typography-line-height-tight)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

const optionDescriptionTextVariant = {
  as: "span",
  style: {
    display: "block",
    minWidth: 0,
    overflow: "hidden",
    color: "var(--c6-color-text-muted)",
    fontSize: "var(--c6-typography-size-caption)",
    lineHeight: "var(--c6-typography-line-height-tight)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

const optionCountTextVariant = {
  as: "span",
  style: {
    color: "var(--c6-color-text-muted)",
    fontSize: "var(--c6-typography-size-caption)",
    fontWeight: boldWeight,
    lineHeight: "1",
  },
} satisfies TextVariantDefinition;

const Root = styled.div<{ $fullWidth: boolean }>`
  position: relative;
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "inline-flex")};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  max-width: 100%;
  min-width: 0;
`;

const Trigger = styled(IconButton)<{
  $open: boolean;
  $selected: boolean;
  $size: SingleSelectDropdownSize;
  $fullWidth: boolean;
}>`
  display: ${({ $fullWidth }) => ($fullWidth ? "flex" : "inline-flex")};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  max-width: 100%;
  min-width: 0;
  min-height: ${({ $size }) => inlineElementHeight[$size]};
  align-items: center;
  justify-content: space-between;
  gap: ${({ $size }) => dropdownTriggerGap($size)};
  /* 패딩·gap 은 MultiSelect 트리거와 공유한다(common.ts) — 세로는 min-height 이하라 계약 높이에 바인딩. */
  padding: ${({ $size }) => dropdownTriggerPadding($size)};
  ${controlRadius}
  border: 1px solid
    ${({ $open, $selected, theme }) =>
    $open || $selected ? theme.color.border.selected : theme.color.border.subtle};
  background-color: ${({ $open, theme }) =>
    $open ? theme.color.bg.surfaceRaisedHigh : theme.color.bg.surfaceSunken};
  color: ${({ $open, $selected, theme }) =>
    $open || $selected ? theme.color.text.accent : theme.color.text.default};
  font: inherit;
  ${controlTransition("background-color", "border-color", "color")}

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.color.border.selected};
    background-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
    color: ${({ theme }) => theme.color.text.accent};
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.color.border.focus};
    outline: none;
  }

  &:disabled {
    border-color: ${({ theme }) => theme.color.border.disabled};
    background-color: ${({ theme }) => theme.color.bg.control.disabled};
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }
`;

// Chevron은 trigger의 accent 글자색을 상속하지 않고 토큰에서 자체 색을 결정한다. 크기는 field 안의
// 보조 affordance이므로 공용 indicator scale(sm 12 / md 14)을 쓴다.
const Chevron = styled(ChevronGlyph)<{
  $disabled: boolean;
  $open: boolean;
  $size: SingleSelectDropdownSize;
}>`
  flex: 0 0 auto;
  color: ${({ $disabled, theme }) =>
    $disabled ? theme.color.text.disabled : theme.color.text.muted};
  height: ${({ $size }) => inlineElementIndicatorSize[$size]};
  width: ${({ $size }) => inlineElementIndicatorSize[$size]};
  transition: transform 120ms ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "none")};
`;

// 메뉴 오버레이는 AnchoredPopup(portal + fixed + flip/clamp + modalStack)에 위임한다.
// Panel의 기본 표면(surfaceRaised/그림자)을 기존 dropdown 메뉴 외관으로 보존 — 이관은 포지셔닝만
// 바꾸고 resting 외관은 유지(시각 리디자인은 별개).
const MenuPopup = styled(AnchoredPopup)`
  && {
    background-color: ${({ theme }) => theme.color.bg.surface};
    border-color: ${({ theme }) => theme.color.border.default};
    box-shadow: ${({ theme }) => theme.elevation.shadow.overlay};
  }
`;

// Panel(flex column) 안에서 search(auto) + 스크롤 목록(1fr)을 기존 grid 레이아웃 그대로 채운다.
const MenuInner = styled.div<{ $searchable: boolean }>`
  display: grid;
  grid-template-rows: ${({ $searchable }) =>
    $searchable ? "auto minmax(0, 1fr)" : "minmax(0, 1fr)"};
  min-height: 0;
  flex: 1;
`;

const OptionList = styled.div`
  display: grid;
  min-width: 0;
  min-height: 0;
  ${yScrollbarStyles}
`;

const Option = styled.button<{ $selected: boolean }>`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 0;
  background-color: transparent;
  color: ${({ theme }) => theme.color.text.default};
  font: inherit;
  text-align: left;
  cursor: pointer;

  ${({ $selected, theme }) =>
    $selected
      ? css`
          background-color: ${theme.color.bg.surfaceRaised};
        `
      : ""}

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.color.bg.surfaceRaised};
  }

  &:disabled {
    color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }
`;

const OptionText = styled.span`
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
  gap: 0.25rem;
`;

const OptionCount = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-width: 1.6rem;
  min-height: 1.35rem;
  padding: 0 0.5rem;
  ${controlRadius}
  background-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  font-variant-numeric: tabular-nums;
`;

// 선택 표시(체크마크)는 행 왼쪽에 두고, 미선택 행도 visibility로 자리를 예약해 텍스트를 정렬한다
// (macOS/네이티브 select식 단일선택 메뉴 패턴 — 체크박스 아님). 그 덕에 count 배지는 행 오른쪽 끝에
// 붙어 행마다 정렬된다(우측에 체크가 없으니 배지가 "붕 뜨지" 않는다).
const CheckIcon = styled(CheckGlyph)<{ $hidden: boolean }>`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.color.text.accent};
  ${({ $hidden }) => ($hidden ? "visibility: hidden;" : "")}
`;

const EmptyMessage = styled(Text)`
  padding: 0.75rem;
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.color.text.danger};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
`;

type OptionInjectableProps = HTMLAttributes<HTMLElement>;
type SearchActionInjectableProps = HTMLAttributes<HTMLElement>;

export function SingleSelectDropdown({
  id,
  label,
  options,
  value,
  onValueChange,
  onOpenChange,
  placeholder,
  neutralValue,
  mode = "field",
  emptyMessage = defaultEmptyMessage,
  required = false,
  disabled = false,
  fullWidth = false,
  size = "md",
  className,
  style,
  menuAlign = "left",
  menuWidth = defaultMenuWidth,
  maxMenuHeight = defaultMaxMenuHeight,
  placement,
  searchable = false,
  searchPlaceholder = defaultSearchPlaceholder,
  clearSearchLabel = defaultClearSearchLabel,
  searchAction,
  noResultsMessage = defaultNoResultsMessage,
  labelStyle,
  filterOption,
  renderOption,
}: SingleSelectDropdownProps) {
  const { controlId, disabled: isDisabled } = useFieldControl({
    componentName: "SingleSelectDropdown",
    id,
    disabled,
  });
  const { anchor, open, openFromElement, close: closeAnchor } = useAnchoredPopupAnchor();
  const [searchQuery, setSearchQuery] = useState("");
  const menuId = useId();
  useDropdownOpenChange(open, onOpenChange);
  const optionLabelByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options],
  );
  const { text: triggerText, markup: triggerMarkup } = buildTriggerLabel({
    label,
    placeholder,
    value,
    mode,
    optionLabelByValue,
  });
  const showRequiredMarker = required && value === null;
  const triggerAriaLabel = showRequiredMarker ? `${triggerText} (required)` : triggerText;
  // facet 트리거 강조(accent)는 "의미 있는 값이 선택됐을 때"만. `neutralValue`(옵트인)와 같은 값이면
  // 메뉴엔 체크되지만 강조 안 함("없음/전체" 같은 default 옵션용). field는 선택 여부와 무관하게 닫힌
  // trigger가 neutral이다. Filter가 "All"(value "")에 neutralValue="" 지정.
  const isEmphasizedSelection = mode === "facet" && value !== null && value !== neutralValue;
  // 지정한 구간만 기본값을 덮어쓰도록 얕게 병합한다.
  const resolvedLabelStyle = useMemo(
    () => ({
      ...(mode === "facet"
        ? isDisabled
          ? disabledTriggerLabelStyle
          : defaultTriggerLabelStyle
        : undefined),
      ...labelStyle,
    }),
    [isDisabled, labelStyle, mode],
  );
  const filteredOptions = useMemo(() => {
    if (!searchable) {
      return options;
    }

    const filter = filterOption ?? defaultDropdownFilterOption;
    return options.filter((option) => filter(option, searchQuery));
  }, [filterOption, options, searchable, searchQuery]);
  const resolvedPlacement = placement ?? (menuAlign === "right" ? "bottom-end" : "bottom-start");
  const resolvedMenuWidth = resolveDropdownMenuWidth(menuWidth);

  const close = useCallback(() => {
    closeAnchor();
    setSearchQuery("");
  }, [closeAnchor]);

  useLayoutEffect(() => {
    if (isDisabled) {
      close();
    }
  }, [close, isDisabled]);

  const selectOption = useCallback(
    (option: SingleSelectDropdownOption) => {
      if (isDisabled || option.disabled) {
        return;
      }
      onValueChange(option.value);
      close();
    },
    [close, isDisabled, onValueChange],
  );

  return (
    <Root className={className} style={style} $fullWidth={fullWidth}>
      <Trigger
        id={controlId}
        $open={open}
        $selected={isEmphasizedSelection}
        $size={size}
        $fullWidth={fullWidth}
        aria-label={triggerAriaLabel}
        disabled={isDisabled}
        variant="surface"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        // 열려 있을 때만 pointerdown 전파를 끊는다 — 자기 토글-닫힘 시 pointerdown이 document로 새면
        // 메뉴가 먼저 닫히고 이어지는 click이 다시 열어 토글이 깨진다. 닫혀 있을 땐(=새로 여는 트리거)
        // 전파시켜 다른 열린 popover가 이 클릭을 outside-dismiss로 감지하게 둔다(여러 dropdown 누적 방지).
        onPointerDown={(event) => {
          if (open) event.stopPropagation();
        }}
        onClick={(event) => (open ? close() : openFromElement(event))}
      >
        <Text variant={dropdownTriggerTextVariant[size]} labelStyle={resolvedLabelStyle}>
          {triggerMarkup}
          {showRequiredMarker ? (
            <span aria-hidden="true">
              {" ("}
              <RequiredMark>*</RequiredMark>
              {")"}
            </span>
          ) : null}
        </Text>
        <Chevron $disabled={isDisabled} $open={open} $size={size} />
      </Trigger>

      {/* Popup descendants are sub-controls, not the Field's primary control. Reset the outer Field
          context so custom option/search content cannot inherit the trigger id or disabled contract. */}
      <FieldContextProvider value={null}>
        <MenuPopup
          open={open && !isDisabled}
          anchor={anchor}
          onClose={close}
          // Panel은 presentation — 내부 OptionList가 listbox 시맨틱을 소유하고 트리거의
          // aria-haspopup="listbox"/aria-controls가 그것을 가리킨다(select를 dialog로 announce하지 않게).
          role="presentation"
          placement={resolvedPlacement}
          offset={4}
          // autoFocus는 AnchoredPopup의 focus 진입 정책이며 native autofocus가 아니다. 메뉴가 portal로
          // 떠 트리거 뒤 DOM 흐름이 끊기므로, 열면 첫 focusable로 focus를 넘겨야 키보드 사용자가 옵션에
          // 닿는다(안 넘기면 트리거에 남고 다음 Tab이 페이지로 샌다). searchable=검색 입력, 아니면 첫 옵션.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus="firstFocusable"
          // 열 때 focus를 팝업으로 넣었으니 닫힐 때(Escape/옵션 선택) 트리거로 되돌린다 — page-level은
          // AnchoredPopup 기본이 복원 안 해 focus가 body로 떨어진다. 바깥 클릭(focus가 다른 연결된 컨트롤로
          // 이동)은 AnchoredPopup이 감지해 트리거로 뺏지 않는다.
          returnFocus
          width={resolvedMenuWidth}
          maxWidth="calc(100vw - 2rem)"
          maxHeight={maxMenuHeight}
        >
          <MenuInner $searchable={searchable}>
            {searchable ? (
              <DropdownSearchField
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder={searchPlaceholder}
                clearLabel={clearSearchLabel}
                action={
                  searchAction != null ? injectSearchActionClose(searchAction, close) : undefined
                }
              />
            ) : null}
            <OptionList id={menuId} role="listbox" aria-label={label}>
              {options.length === 0 ? (
                <EmptyMessage variant={emptyTextVariant}>{emptyMessage}</EmptyMessage>
              ) : filteredOptions.length === 0 ? (
                <EmptyMessage variant={emptyTextVariant}>{noResultsMessage}</EmptyMessage>
              ) : (
                filteredOptions.map((option) => {
                  const selected = option.value === value;
                  const optionDisabled = option.disabled ?? false;
                  const onSelect = () => selectOption(option);
                  const context: SingleSelectDropdownOptionRenderContext = {
                    selected,
                    disabled: optionDisabled,
                    onSelect,
                  };

                  if (renderOption != null) {
                    const customOption = renderOption(option, context);
                    if (!isValidElement<OptionInjectableProps>(customOption)) {
                      return null;
                    }
                    return cloneElement(customOption, {
                      key: option.value,
                      role: "option",
                      "aria-selected": selected,
                      "aria-disabled": optionDisabled || undefined,
                      onClick: (event: ReactMouseEvent<HTMLElement>) => {
                        if (optionDisabled) {
                          event.preventDefault();
                          return;
                        }
                        customOption.props.onClick?.(event);
                        if (event.defaultPrevented) {
                          return;
                        }
                        onSelect();
                      },
                    });
                  }

                  return (
                    <Option
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      $selected={selected}
                      disabled={optionDisabled}
                      onClick={onSelect}
                    >
                      {/* 선택 체크마크는 왼쪽 — 미선택 행도 자리를 예약(visibility)해 텍스트/배지 정렬. */}
                      <CheckIcon $hidden={!selected} aria-hidden />
                      <OptionText>
                        <Text variant={optionLabelTextVariant}>{option.label}</Text>
                        {option.description ? (
                          <Text variant={optionDescriptionTextVariant}>{option.description}</Text>
                        ) : null}
                      </OptionText>
                      {typeof option.count === "number" ? (
                        <OptionCount>
                          <Text variant={optionCountTextVariant}>{option.count}</Text>
                        </OptionCount>
                      ) : null}
                    </Option>
                  );
                })
              )}
            </OptionList>
          </MenuInner>
        </MenuPopup>
      </FieldContextProvider>
    </Root>
  );
}

/**
 * trigger 라벨을 평문(aria용)과 마크업(표시용) 두 형태로 만든다.
 *
 * 값이 선택되면 aria용 평문은 항상 "label: value"로 유지한다. facet 표시는
 * `<prefix>label: </prefix><value>value</value>`, field 표시는 `<value>value</value>`로
 * 만들어 `Text.labelStyle`가 실제로 보이는 구간에만 스타일을 입히게 한다. value === null이면
 * placeholder를 평문 그대로 둔다(강조 구간 없음).
 *
 * 한계: label/value가 짝이 맞는 `<태그>…</태그>` 문자열을 포함하면 파서가 이를
 * 마크업으로 오인할 수 있다. 개발자 제공 라벨에서는 사실상 발생하지 않는다.
 */
function buildTriggerLabel({
  label,
  placeholder,
  value,
  mode,
  optionLabelByValue,
}: {
  label: string;
  placeholder?: string;
  value: string | null;
  mode: SingleSelectDropdownMode;
  optionLabelByValue: ReadonlyMap<string, string>;
}): { text: string; markup: string } {
  if (value === null) {
    const placeholderText = placeholder ?? label;
    return {
      text: mode === "field" && placeholder != null ? `${label}: ${placeholder}` : placeholderText,
      markup: placeholderText,
    };
  }
  const selectedLabel = optionLabelByValue.get(value) ?? value;
  return {
    text: `${label}: ${selectedLabel}`,
    markup:
      mode === "facet"
        ? `<prefix>${label}: </prefix><value>${selectedLabel}</value>`
        : `<value>${selectedLabel}</value>`,
  };
}

function injectSearchActionClose(action: ReactNode, close: () => void): ReactNode {
  if (!isValidElement<SearchActionInjectableProps>(action)) {
    return action;
  }

  return cloneElement(action, {
    onMouseDown: (event: ReactMouseEvent<HTMLElement>) => {
      action.props.onMouseDown?.(event);
      if (!event.defaultPrevented) {
        event.preventDefault();
      }
    },
    onClick: (event: ReactMouseEvent<HTMLElement>) => {
      action.props.onClick?.(event);
      if (!event.defaultPrevented) {
        close();
      }
    },
  });
}

SingleSelectDropdown.displayName = "SingleSelectDropdown";
